import type { Collection, ContentItem, ContentItemId } from '@kidshub/contracts';
import { isLongCompilation } from '@kidshub/contracts';
import {
  type CSSProperties,
  type FormEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { formatClock, splitDuration } from '../lib/duration.js';
import { toErrorKey } from '../lib/error-code.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Badge, Button, ButtonLink, EmptyState, Icon, Segmented, Switch } from '../ui/kit.js';
import { moveTo, type QuickSort, quickSort } from './collection-sort.js';
import { collectionFelt } from './collections-rail.js';
import { RenameField, TITLE_MAX_LENGTH } from './rename-field.js';
import { SuggestionChip } from './suggestion-chip.js';
import { suggestTitle } from './title-suggestion.js';
import './collection.css';

/**
 * One collection, in the order a child's tablet shows it.
 *
 * Two things a parent could not do before, both because of what YouTube hands
 * over: name the episodes something a person would say, and put them in an
 * order that makes sense for their child. The channel's order is newest first
 * and its titles are written for an algorithm.
 *
 * Decided on the mock board `.docs/design/library-names` (2026-10-09): names
 * are edited in place, the list is dragged, suggestions sit on every row whose
 * name is still the channel's, and new imports land at the top.
 *
 * Every change is saved as it is made and can be undone from the toast, so
 * there is no "save" step to forget and nothing to lose by trying.
 */

/** How many tiles the tablet preview draws: the first screen of a collection. */
const PREVIEW_TILES = 6;
const UNDO_VISIBLE_MS = 5000;

interface Undo {
  message: string;
  run: () => Promise<void>;
}

export const CollectionPage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { collectionId } = useParams();

  const content = useResource('content', () => apiClient.catalog.list());
  const collectionsResource = useResource('collections', () => apiClient.collections.list());
  const children = useResource('children', () => apiClient.children.list());
  // How often each episode was opened lately, by anyone: the owner wanted it
  // beside each row (board `.docs/design/watch-analytics`, 2026-10-09).
  const recentOpens = useResource('itemOpens:7', () => apiClient.analytics.itemOpens({ days: 7 }));

  const items = content.resource.status === 'ready' ? content.resource.data.items : [];
  const allCollections =
    collectionsResource.resource.status === 'ready'
      ? collectionsResource.resource.data.collections
      : [];
  const familyChildren =
    children.resource.status === 'ready' ? children.resource.data.children : [];

  const collection = allCollections.find((entry) => entry.id === collectionId) ?? null;
  const opensById = new Map(
    (recentOpens.resource.status === 'ready' ? recentOpens.resource.data.items : []).map(
      (entry) => [entry.contentItemId, entry.opens],
    ),
  );
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const order = (collection?.itemIds ?? []).filter((id) => itemsById.has(id));

  const [editingId, setEditingId] = useState<ContentItemId | null>(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [isRenamingCollection, setIsRenamingCollection] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);

  useEffect(() => {
    if (undo === null) {
      return;
    }
    const timer = setTimeout(() => setUndo(null), UNDO_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [undo]);

  // What the writes below read. An undo runs seconds after the render that
  // created it, and must not put back whatever else changed in between.
  const current = useRef({ items, allCollections });
  current.current = { items, allCollections };

  const replaceCollection = (updated: Collection) => {
    collectionsResource.replace({
      collections: current.current.allCollections.map((entry) =>
        entry.id === updated.id ? updated : entry,
      ),
    });
  };

  const currentCollection = () =>
    current.current.allCollections.find((entry) => entry.id === collectionId) ?? null;

  // ---------------------------------------------------------------- writes

  const saveOrder = async (next: ContentItemId[], previous: ContentItemId[], message: string) => {
    const target = currentCollection();
    if (target === null || next.join() === previous.join()) {
      return;
    }
    setErrorKey(null);
    replaceCollection({ ...target, itemIds: next });

    try {
      replaceCollection(
        await apiClient.collections.setItems({ collectionId: target.id, itemIds: next }),
      );
      setUndo({ message, run: () => saveOrder(previous, next, t('collectionPage.undone')) });
    } catch (error) {
      replaceCollection({ ...target, itemIds: previous });
      setErrorKey(toErrorKey(error));
    }
  };

  const rename = async (renames: { item: ContentItem; title: string }[], message: string) => {
    setErrorKey(null);
    const done: { item: ContentItem; title: string }[] = [];

    try {
      for (const { item, title } of renames) {
        const updated = await apiClient.catalog.rename({ contentItemId: item.id, title });
        content.replace({
          items: current.current.items.map((entry) => (entry.id === updated.id ? updated : entry)),
        });
        current.current.items = current.current.items.map((entry) =>
          entry.id === updated.id ? updated : entry,
        );
        done.push({ item: updated, title: item.title });
      }
    } catch (error) {
      setErrorKey(toErrorKey(error));
    }

    if (done.length > 0) {
      setUndo({ message, run: () => rename(done, t('collectionPage.undone')) });
    }
  };

  const updatePreferences = async (changes: {
    title?: string;
    newItemsFirst?: boolean;
    longItemsLast?: boolean;
  }) => {
    const target = currentCollection();
    if (target === null) {
      return;
    }
    setErrorKey(null);
    try {
      replaceCollection(
        await apiClient.collections.update({ collectionId: target.id, ...changes }),
      );
    } catch (error) {
      setErrorKey(toErrorKey(error));
    }
  };

  // ------------------------------------------------------------------ drag

  const listRef = useRef<HTMLOListElement>(null);
  const [drag, setDrag] = useState<{
    id: ContentItemId;
    order: ContentItemId[];
    before: ContentItemId[];
  } | null>(null);
  const shownOrder = drag?.order ?? order;
  const dragRef = useRef(drag);
  dragRef.current = drag;

  // On the window rather than the handle: the row moves in the DOM as it is
  // dragged, and an element that moves can lose the pointer it captured.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-bound only when a drag starts or ends
  useEffect(() => {
    if (drag === null) {
      return;
    }

    const onMove = (event: PointerEvent) => {
      const rows = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-row]') ?? [])];
      // Past every other row whose middle is above the pointer.
      const target = rows
        .filter((row) => row.getAttribute('data-row') !== drag.id)
        .filter((row) => {
          const box = row.getBoundingClientRect();
          return event.clientY > box.top + box.height / 2;
        }).length;
      setDrag((dragging) =>
        dragging === null || dragging.order.indexOf(dragging.id) === target
          ? dragging
          : { ...dragging, order: moveTo(dragging.order, dragging.id, target) },
      );
    };

    const onEnd = () => {
      const ended = dragRef.current;
      setDrag(null);
      if (ended !== null) {
        void saveOrder(
          ended.order,
          ended.before,
          t('collectionPage.moved', { position: ended.order.indexOf(ended.id) + 1 }),
        );
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  }, [drag === null]);

  // A row moved by keyboard keeps its handle focused, so the next press works.
  const focusAfterMove = useRef<ContentItemId | null>(null);
  useEffect(() => {
    if (focusAfterMove.current !== null) {
      listRef.current
        ?.querySelector<HTMLElement>(`[data-handle="${focusAfterMove.current}"]`)
        ?.focus();
      focusAfterMove.current = null;
    }
  });

  const moveByKey = (id: ContentItemId, step: number) => {
    const next = moveTo(order, id, order.indexOf(id) + step);
    focusAfterMove.current = id;
    void saveOrder(next, order, t('collectionPage.moved', { position: next.indexOf(id) + 1 }));
  };

  // --------------------------------------------------------------- reading

  if (collectionsResource.resource.status === 'ready' && collection === null) {
    return (
      <AppShell title={t('library.title')}>
        <EmptyState
          title={t('collectionPage.notFound')}
          actions={<ButtonLink to="/library">{t('collectionPage.backToLibrary')}</ButtonLink>}
        />
      </AppShell>
    );
  }

  const channelNames = collection === null ? [] : [collection.title];
  const suggestionFor = (item: ContentItem) =>
    item.originalTitle !== null && item.title !== item.originalTitle
      ? null
      : suggestTitle(item.title, channelNames, locale);
  const untidy = shownOrder.flatMap((id) => {
    const item = itemsById.get(id);
    const suggestion = item === undefined ? null : suggestionFor(item);
    return item === undefined || suggestion === null ? [] : [{ item, suggestion }];
  });

  const audience = familyChildren.filter(
    (entry) => collection !== null && entry.profile.enabledCollectionIds.includes(collection.id),
  );
  const totalSeconds = order.reduce(
    (sum, id) => sum + (itemsById.get(id)?.durationSeconds ?? 0),
    0,
  );
  const total = splitDuration(totalSeconds);
  const listFormat = new Intl.ListFormat(locale, { type: 'conjunction' });
  const subtitle = [
    t('collectionPage.episodes', { count: order.length }),
    t('collectionPage.total', total),
    audience.length === 0
      ? t('collectionPage.nobodySees')
      : t('collectionPage.sees', {
          names: listFormat.format(audience.map((entry) => entry.child.name)),
        }),
  ].join(' · ');

  const sortBy = (sort: QuickSort) => {
    if (collection === null) {
      return;
    }
    const sorted = quickSort(
      order.flatMap((id) => itemsById.get(id) ?? []),
      sort,
      locale,
      collection.longItemsLast,
    );
    void saveOrder(sorted, order, t(`collectionPage.sorted.${sort}`));
  };

  return (
    <AppShell
      title={collection?.title ?? t('common.loading')}
      subtitle={collection === null ? undefined : subtitle}
      actions={
        <>
          <Button variant="ghost" icon="pen" onClick={() => setIsRenamingCollection(true)}>
            {t('collectionPage.renameCollection')}
          </Button>
          <Button
            variant="secondary"
            icon="spark"
            disabled={untidy.length === 0}
            onClick={() => setIsBulkOpen(true)}
          >
            {untidy.length === 0
              ? t('collectionPage.tidyNames')
              : t('collectionPage.tidyNamesCount', { count: untidy.length })}
          </Button>
        </>
      }
    >
      <ButtonLink variant="ghost" small icon="back" to="/library" className="cp-back">
        {t('library.title')}
      </ButtonLink>

      {errorKey === null ? null : (
        <p className="help err" role="alert">
          {t(errorKey)}
        </p>
      )}

      <div className="cp-layout">
        <article className="card cp-list-card">
          <div className="card-head">
            <div>
              <h2 className="t-h2">{t('collectionPage.episodesHeading')}</h2>
              <p className="t-meta" id="cp-hint">
                {t('collectionPage.hint')}
              </p>
            </div>
          </div>

          {order.length === 0 && content.resource.status === 'ready' ? (
            <EmptyState
              title={t('library.collectionEmptyTitle', { title: collection?.title ?? '' })}
              detail={t('library.collectionEmptyDetail')}
              actions={
                <ButtonLink variant="primary" icon="plus" to="/library/import">
                  {t('library.importSource')}
                </ButtonLink>
              }
            />
          ) : (
            <ol className="cp-list" ref={listRef} aria-label={t('collectionPage.orderLabel')}>
              {shownOrder.map((id, index) => {
                const item = itemsById.get(id);
                if (item === undefined || collection === null) {
                  return null;
                }
                return (
                  <EpisodeRow
                    key={id}
                    item={item}
                    position={index + 1}
                    felt={collectionFelt(collection.id)}
                    suggestion={suggestionFor(item)}
                    opens={opensById.get(id) ?? 0}
                    isEditing={editingId === id}
                    isDragging={drag?.id === id}
                    onEdit={() => setEditingId(id)}
                    onCancelEdit={() => setEditingId(null)}
                    onRename={(title) => {
                      setEditingId(null);
                      void rename([{ item, title }], t('collectionPage.renamed'));
                    }}
                    onDragStart={() => setDrag({ id, order, before: order })}
                    onMoveKey={(step) => moveByKey(id, step)}
                  />
                );
              })}
            </ol>
          )}
        </article>

        <div className="cp-side">
          {collection === null ? null : (
            <TabletPreview
              collection={collection}
              order={shownOrder}
              itemsById={itemsById}
              audience={audience.map((entry) => ({
                id: entry.child.id,
                name: entry.child.name,
              }))}
            />
          )}

          {collection === null ? null : (
            <article className="card">
              <div className="card-head">
                <div>
                  <h2 className="t-h2">{t('collectionPage.quickSort')}</h2>
                  <p className="t-meta">{t('collectionPage.quickSortHint')}</p>
                </div>
              </div>
              <div className="cp-quick">
                {(['NAME', 'DURATION', 'ADDED'] as const).map((sort) => (
                  <Button key={sort} variant="secondary" small onClick={() => sortBy(sort)}>
                    {t(`collectionPage.sort.${sort}`)}
                  </Button>
                ))}
              </div>
              <div className="row">
                <div className="row-text">
                  <b>{t('collectionPage.newItems')}</b>
                  <span>{t('collectionPage.newItemsHint')}</span>
                </div>
                <Segmented<'FIRST' | 'LAST'>
                  label={t('collectionPage.newItems')}
                  value={collection.newItemsFirst ? 'FIRST' : 'LAST'}
                  options={[
                    { value: 'FIRST', label: t('collectionPage.newItemsFirst') },
                    { value: 'LAST', label: t('collectionPage.newItemsLast') },
                  ]}
                  onChange={(value) => void updatePreferences({ newItemsFirst: value === 'FIRST' })}
                />
              </div>
              <div className="row">
                <div className="row-text">
                  <b>{t('collectionPage.longLast')}</b>
                  <span>{t('collectionPage.longLastHint')}</span>
                </div>
                <Switch
                  checked={collection.longItemsLast}
                  label={t('collectionPage.longLast')}
                  onChange={(checked) => void updatePreferences({ longItemsLast: checked })}
                />
              </div>
            </article>
          )}
        </div>
      </div>

      {isBulkOpen ? (
        <TidyNamesSheet
          entries={untidy}
          onClose={() => setIsBulkOpen(false)}
          onApply={(renames) => {
            setIsBulkOpen(false);
            void rename(renames, t('collectionPage.tidied', { count: renames.length }));
          }}
        />
      ) : null}

      {isRenamingCollection && collection !== null ? (
        <CollectionNameSheet
          title={collection.title}
          onClose={() => setIsRenamingCollection(false)}
          onSave={(title) => {
            setIsRenamingCollection(false);
            void updatePreferences({ title });
          }}
        />
      ) : null}

      <div className={undo === null ? 'toast' : 'toast show'} role="status">
        {undo === null ? null : (
          <>
            <Icon name="check" small />
            <span>{undo.message}</span>
            <button
              type="button"
              className="btn btn-sm btn-onink"
              onClick={() => {
                const { run } = undo;
                setUndo(null);
                void run();
              }}
            >
              {t('collectionPage.undo')}
            </button>
          </>
        )}
      </div>
    </AppShell>
  );
};

// ---------------------------------------------------------------------------

interface EpisodeRowProps {
  item: ContentItem;
  position: number;
  felt: string;
  suggestion: string | null;
  /** Opened in the last week, by anyone in the family. */
  opens: number;
  isEditing: boolean;
  isDragging: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onRename: (title: string) => void;
  onDragStart: () => void;
  onMoveKey: (step: number) => void;
}

const EpisodeRow = ({
  item,
  position,
  felt,
  suggestion,
  opens,
  isEditing,
  isDragging,
  onEdit,
  onCancelEdit,
  onRename,
  onDragStart,
  onMoveKey,
}: EpisodeRowProps) => {
  const { t } = useTranslation();
  const isRenamed = item.originalTitle !== null && item.title !== item.originalTitle;
  const duration = item.durationSeconds === null ? null : formatClock(item.durationSeconds);

  return (
    <li className={isDragging ? 'cp-row is-dragging' : 'cp-row'} data-row={item.id}>
      <button
        type="button"
        className="cp-handle"
        data-handle={item.id}
        aria-label={t('collectionPage.moveHandle', { title: item.title, position })}
        aria-describedby="cp-hint"
        onPointerDown={(event) => {
          if (event.button === 0) {
            event.preventDefault();
            onDragStart();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            onMoveKey(event.key === 'ArrowUp' ? -1 : 1);
          }
        }}
      >
        <Icon name="grip" />
      </button>

      <div className="cp-thumb">
        <span className="cp-num num">{position}</span>
        <div className="poster" style={{ '--pc': felt } as CSSProperties} aria-hidden="true">
          {item.posterUrl === null ? (
            <span className="lib-poster-mark">
              <Icon name="play" small />
            </span>
          ) : (
            <img className="lib-poster-img" src={item.posterUrl} alt="" loading="lazy" />
          )}
        </div>
      </div>

      <div className="cp-text">
        {isEditing ? (
          <RenameField
            item={item}
            suggestion={suggestion}
            onSave={onRename}
            onCancel={onCancelEdit}
            autoFocus
          />
        ) : (
          <>
            <button
              type="button"
              className={isRenamed ? 'cp-name' : 'cp-name is-raw'}
              onClick={onEdit}
            >
              {item.title}
            </button>
            <div className="cp-sub">
              {duration === null ? null : <span className="num cp-dur-inline">{duration}</span>}
              {opens === 0 ? null : (
                <span className="cp-opens">{t('collectionPage.opens', { count: opens })}</span>
              )}
              {isLongCompilation(item.durationSeconds) ? (
                <Badge tone="amber">{t('import.longCompilation')}</Badge>
              ) : null}
              {isRenamed ? (
                <span className="cp-orig" title={item.originalTitle ?? undefined}>
                  {t('collectionPage.original', { title: item.originalTitle })}
                </span>
              ) : null}
            </div>
            {suggestion === null ? null : (
              <SuggestionChip labelled onClick={() => onRename(suggestion)}>
                {suggestion}
              </SuggestionChip>
            )}
          </>
        )}
      </div>

      <div className="cp-actions">
        {duration === null ? null : <span className="cp-dur num">{duration}</span>}
        {isEditing ? null : (
          <button
            type="button"
            className="icon-btn"
            aria-label={t('collectionPage.rename', { title: item.title })}
            onClick={onEdit}
          >
            <Icon name="pen" />
          </button>
        )}
      </div>
    </li>
  );
};

// ---------------------------------------------------------------------------

/**
 * What the child's tablet will draw, first screen only.
 *
 * Only what the child can actually see: approved for them, in this order. A
 * preview that showed unapproved episodes would promise the parent something
 * the tablet then does not do.
 */
const TabletPreview = ({
  collection,
  order,
  itemsById,
  audience,
}: {
  collection: Collection;
  order: ContentItemId[];
  itemsById: Map<ContentItemId, ContentItem>;
  audience: { id: string; name: string }[];
}) => {
  const { t } = useTranslation();
  const viewer = audience[0];
  const visible =
    viewer === undefined
      ? []
      : order.flatMap((id) => {
          const item = itemsById.get(id);
          return item?.approvedForChildIds.some((childId) => childId === viewer.id) ? [item] : [];
        });

  return (
    <article className="card">
      <div className="card-head">
        <div>
          <h2 className="t-h2">{t('collectionPage.onTablet')}</h2>
          <p className="t-meta">
            {viewer === undefined
              ? t('collectionPage.onTabletNobody')
              : t('collectionPage.onTabletFor', { name: viewer.name })}
          </p>
        </div>
      </div>
      {viewer === undefined ? null : (
        <div className="cp-tablet">
          <div className="cp-tablet-screen">
            {visible.slice(0, PREVIEW_TILES).map((item, index) => (
              <div key={item.id} className="cp-tile">
                <div className="cp-tile-art">
                  <span className="cp-num num">{index + 1}</span>
                  <div
                    className="poster"
                    style={{ '--pc': collectionFelt(collection.id) } as CSSProperties}
                    aria-hidden="true"
                  >
                    {item.posterUrl === null ? null : (
                      <img className="lib-poster-img" src={item.posterUrl} alt="" loading="lazy" />
                    )}
                  </div>
                </div>
                <b>{item.title}</b>
              </div>
            ))}
          </div>
          <p className="cp-tablet-cap">
            {visible.length === 0
              ? t('collectionPage.onTabletEmpty')
              : visible.length > PREVIEW_TILES
                ? t('collectionPage.onTabletMore', { count: visible.length - PREVIEW_TILES })
                : t('collectionPage.onTabletAll')}
          </p>
        </div>
      )}
    </article>
  );
};

// ---------------------------------------------------------------------------

const TidyNamesSheet = ({
  entries,
  onClose,
  onApply,
}: {
  entries: { item: ContentItem; suggestion: string }[];
  onClose: () => void;
  onApply: (renames: { item: ContentItem; title: string }[]) => void;
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [picked, setPicked] = useState<ReadonlySet<ContentItemId>>(
    () => new Set(entries.map((entry) => entry.item.id)),
  );
  const [names, setNames] = useState<ReadonlyMap<ContentItemId, string>>(
    () => new Map(entries.map((entry) => [entry.item.id, entry.suggestion])),
  );

  const chosen = entries.filter(
    (entry) => picked.has(entry.item.id) && (names.get(entry.item.id) ?? '').trim() !== '',
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply(
      chosen.map((entry) => ({
        item: entry.item,
        title: (names.get(entry.item.id) ?? entry.suggestion).trim(),
      })),
    );
  };

  return (
    <Sheet titleId={titleId} onClose={onClose} wide>
      <h2 className="t-h2" id={titleId}>
        {t('collectionPage.tidyNames')}
      </h2>
      <p className="t-meta">{t('collectionPage.tidyHint')}</p>
      <form className="sheet-body" onSubmit={submit}>
        <div>
          {entries.map(({ item }) => (
            <div key={item.id} className="cp-bulk-row">
              <input
                type="checkbox"
                className="cbx"
                aria-label={t('collectionPage.tidyPick', { title: item.title })}
                checked={picked.has(item.id)}
                onChange={(event) => {
                  const next = new Set(picked);
                  if (event.target.checked) {
                    next.add(item.id);
                  } else {
                    next.delete(item.id);
                  }
                  setPicked(next);
                }}
              />
              <div className="cp-bulk-text">
                <s>{item.title}</s>
                <input
                  className="input"
                  aria-label={t('collectionPage.name')}
                  maxLength={TITLE_MAX_LENGTH}
                  value={names.get(item.id) ?? ''}
                  onChange={(event) => setNames(new Map(names).set(item.id, event.target.value))}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="sheet-actions">
          <Button variant="primary" type="submit" disabled={chosen.length === 0}>
            {chosen.length === 0
              ? t('collectionPage.tidyNone')
              : t('collectionPage.tidyApply', { count: chosen.length })}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Sheet>
  );
};

const CollectionNameSheet = ({
  title,
  onClose,
  onSave,
}: {
  title: string;
  onClose: () => void;
  onSave: (title: string) => void;
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const inputId = useId();
  const [value, setValue] = useState(title);

  return (
    <Sheet titleId={titleId} onClose={onClose}>
      <h2 className="t-h2" id={titleId}>
        {t('collectionPage.renameCollection')}
      </h2>
      <form
        className="sheet-body"
        onSubmit={(event) => {
          event.preventDefault();
          if (value.trim() !== '') {
            onSave(value.trim());
          }
        }}
      >
        <div className="field">
          <label htmlFor={inputId}>{t('library.collectionTitle')}</label>
          <input
            id={inputId}
            className="input"
            maxLength={40}
            // biome-ignore lint/a11y/noAutofocus: the sheet exists to type this one thing
            autoFocus
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <p className="help">{t('collectionPage.collectionNameHint')}</p>
        </div>
        <div className="sheet-actions">
          <Button variant="primary" type="submit" disabled={value.trim() === ''}>
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Sheet>
  );
};

/** A bottom sheet on a phone, a dialog from 900px; closes on Escape and outside. */
const Sheet = ({
  titleId,
  onClose,
  wide = false,
  children,
}: {
  titleId: string;
  onClose: () => void;
  wide?: boolean;
  children: ReactNode;
}) => (
  <>
    <div className="scrim open" aria-hidden="true" onClick={onClose} />
    <div
      className={wide ? 'sheet open cp-wide' : 'sheet open'}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      }}
    >
      <div className="sheet-grip" aria-hidden="true" />
      {children}
    </div>
  </>
);
